import { monthDrillDownQuery, mrrQuery, MRRQueryOptions } from "@/api/reporting";
import { ChartBox, formatAxisNumber, useContainerSize } from "@/components/charts/ChartBox";
import { Dialog } from "@/components/ui/Dialog";
import { getTokenCurrencyCodes, tokenCurrencyToMoneyCurrency } from "@/modules/money-fns";
import { centsToDollars } from "@common/utils/currency";
import { formatCents } from "@common/utils/i18n";
import { Box, Checkbox, Field, Flex, Label, Select, Separator, Text } from "@givenwell/components";
import { DrillDownItem } from "@givenwell/management-api";
import { colors } from "@givenwell/theme";
import { defineChart, dot, lineY, rect } from "@tanstack/charts";
import { decorative } from "@tanstack/charts/mark/decorative";
import { Chart } from "@tanstack/charts/react";
import { scaleLinear } from "@tanstack/charts/scales/linear";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { scaleUtc } from "d3-scale";
import { Fragment, useMemo, useState } from "react";
import {
  REVENUE_STREAM_COLOR as STREAM_COLOR,
  REVENUE_STREAM_ORDER as STREAM_ORDER,
  REVENUE_STREAMS as STREAMS,
  toRevenueGroup as toGroup,
} from "./revenueStreams";

const endDate = new Date();
endDate.setMonth(endDate.getMonth() + 12);
endDate.setDate(0);
endDate.setHours(23, 59, 59, 999);

const endDate2 = new Date(endDate);
endDate2.setMonth(endDate2.getMonth() + 1);

function localToUTC(date: Date) {
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000);
}

export const Route = createFileRoute("/dashboard/mrr")({
  component: MRRPage,
});

function getPastDates() {
  const dates: Date[] = [];
  const date = new Date("2024-08-01T00:00:00");
  const now = new Date();

  while (date <= now) {
    dates.push(new Date(date));
    date.setMonth(date.getMonth() + 1);
  }

  return dates;
}

const pastDates = getPastDates();

function getAllDates() {
  const dates: Date[] = [];
  const date = new Date("2024-08-01T00:00:00");

  while (date <= endDate) {
    dates.push(new Date(date));
    date.setMonth(date.getMonth() + 1);
  }

  return dates;
}

const allDates = getAllDates();

const sum = (arr: number[]) => arr.reduce((a, b) => a + b, 0);

function getLinearRegression(values: { x: Date; y: number }[]) {
  const n = values.length;
  if (n === 0) return { slope: 0, intercept: 0 };
  const sumX = values.reduce((acc, v, i) => acc + i, 0);
  const sumY = values.reduce((acc, v) => acc + v.y, 0);
  const sumXY = values.reduce((acc, v, i) => acc + i * v.y, 0);
  const sumX2 = values.reduce((acc, v, i) => acc + i * i, 0);
  const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
  const intercept = (sumY - slope * sumX) / n;
  return { slope, intercept };
}

function getDateFromMonth(month: Date, monthProgress: number) {
  const startOfMonth = localToUTC(new Date(month.getFullYear(), month.getMonth(), 1, 0, 0, 0, 0));
  const endOfMonth = localToUTC(new Date(month.getFullYear(), month.getMonth() + 1, 1, 0, 0, 0, 0));
  const time = startOfMonth.getTime() + (endOfMonth.getTime() - startOfMonth.getTime()) * monthProgress;
  return new Date(time);
}

function getExponentialRegressionDots(values: Date[], multiple: number, growth: number) {
  return values.map((v, i) => ({
    x: getDateFromMonth(v, 0.5),
    y: multiple * Math.pow(growth, i),
  }));
}

function getExponentialRegressionLine(values: Date[], multiple: number, growth: number) {
  return values.flatMap((v, i) =>
    [0, 0.125, 0.25, 0.375, 0.5, 0.625, 0.75, 0.875].map(p => ({
      x: getDateFromMonth(v, p),
      y: multiple * Math.pow(growth, i + p - 0.5),
    })),
  );
}

// One stacked segment: a month-wide x interval and stacked y endpoints for one
// revenue group. `month` is the raw API month string, used for drill-down.
type RevenueRect = {
  month: string;
  start: Date;
  end: Date;
  group: string;
  y1: number;
  y2: number;
};

// UTC month bounds for an API month string (the old Plot "month" interval).
function utcMonthStart(month: string) {
  const date = new Date(month);
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
}

function utcMonthEnd(start: Date) {
  return new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 1));
}

// Stack in STREAM_ORDER (bottom -> top); unknown groups go on top.
function stackOrderIndex(group: string) {
  const index = STREAM_ORDER.indexOf(group);
  return index === -1 ? STREAM_ORDER.length : index;
}

// The 2026-08 $1M MRR target.
const TARGET_DOT = { x: localToUTC(new Date("2026-08-15T00:00:00")), y: 1000000 };

// Special currency-selector value meaning "consolidate every currency into NZD".
const CONSOLIDATED = "__consolidated__";

function MRRPage() {
  const [mrrQueryOptions, setQueryOptions] = useState<MRRQueryOptions>({
    consolidated: true,
    amortizeRecurring: true,
    amortizeOneOffs: true,
    includeGivenwell: false,
    endDate: endDate,
  });
  // The selector value: the consolidated sentinel, or a token currency code.
  const selectedCurrency = mrrQueryOptions.consolidated ? CONSOLIDATED : mrrQueryOptions.tokenCurrency;

  const tokenCurrencies = getTokenCurrencyCodes();
  // Currency the totals are denominated in: NZD when consolidating, else the selected token's currency.
  const displayCurrency = useMemo(() => {
    if (mrrQueryOptions.consolidated) return "NZD";
    const selectedToken = getTokenCurrencyCodes().find(code => code === mrrQueryOptions.tokenCurrency);
    return selectedToken && tokenCurrencyToMoneyCurrency(selectedToken);
  }, [mrrQueryOptions.consolidated, mrrQueryOptions.tokenCurrency]);

  const { data, isFetching } = useQuery(mrrQuery(mrrQueryOptions));

  const [visibleStreams, setVisibleStreams] = useState<Record<string, boolean>>(
    Object.fromEntries(STREAMS.map(s => [s.key, true])),
  );
  const [showRegression, setShowRegression] = useState(false);
  const [showTarget, setShowTarget] = useState(false);

  const filteredData = useMemo(() => data?.filter(row => visibleStreams[toGroup(row.stream)]), [data, visibleStreams]);

  // BEGIN finding exponential regression line
  //
  // turns out the way to find the exponential regression line is to do a linear
  // regression on the log of the values, and then exponentiate it back to normal.

  const regressionModel = useMemo(() => {
    const values = pastDates.map(date => {
      const rows =
        filteredData?.filter(r => {
          const month = new Date(r.month);
          return month.getFullYear() === date.getFullYear() && month.getMonth() === date.getMonth();
        }) || [];
      return { x: date, y: centsToDollars(sum(rows.map(r => r.total || 0)) || 0, displayCurrency) };
    });

    const logValues = values
      .filter(v => v.y > 0)
      .map(v => ({
        x: v.x,
        y: Math.log(v.y),
      }));

    const regression = getLinearRegression(logValues);

    const growthRate = Math.exp(regression.slope);
    const multiple = Math.exp(regression.intercept);

    return {
      growthRate,
      line: getExponentialRegressionLine(allDates, multiple, growthRate),
      dots: getExponentialRegressionDots(allDates, multiple, growthRate),
    };
  }, [filteredData, displayCurrency]);

  const growthRate = regressionModel.growthRate;
  const monthOnMonthGrowth = growthRate - 1;
  const yearOnYearGrowth = Math.pow(growthRate, 12) - 1;
  const timeToDouble = Math.log(2) / Math.log(growthRate);
  const timeTo10x = Math.log(10) / Math.log(growthRate);

  // END finding exponential regression line

  const [activeMonth, setActiveMonth] = useState<string | null>(null);

  // Sum each month per revenue group, then stack the groups in STREAM_ORDER.
  const stackRows = useMemo<RevenueRect[]>(() => {
    const byMonth = new Map<string, Map<string, number>>();
    for (const row of filteredData ?? []) {
      const value = centsToDollars(row.total || 0, displayCurrency);
      if (!Number.isFinite(value)) continue;
      const group = toGroup(row.stream);
      let groups = byMonth.get(row.month);
      if (!groups) {
        groups = new Map();
        byMonth.set(row.month, groups);
      }
      groups.set(group, (groups.get(group) ?? 0) + value);
    }

    const rows: RevenueRect[] = [];
    for (const [month, groups] of byMonth) {
      const start = utcMonthStart(month);
      const end = utcMonthEnd(start);
      const orderedGroups = [...groups.keys()].sort((a, b) => stackOrderIndex(a) - stackOrderIndex(b));
      let y = 0;
      for (const group of orderedGroups) {
        const value = groups.get(group) ?? 0;
        rows.push({ month, start, end, group, y1: y, y2: y + value });
        y += value;
      }
    }
    return rows;
  }, [filteredData, displayCurrency]);

  const definition = useMemo(() => {
    const colorDomain = [...STREAM_ORDER];
    for (const row of stackRows) {
      if (!colorDomain.includes(row.group)) colorDomain.push(row.group);
    }

    // Top of the y domain: the future veil spans from 0 to here, i.e. the full
    // plot height, without contributing anything the domain doesn't already have.
    let yMax = 0;
    for (const row of stackRows) yMax = Math.max(yMax, row.y2);
    if (showRegression) {
      for (const point of regressionModel.line) {
        if (Number.isFinite(point.y)) yMax = Math.max(yMax, point.y);
      }
      for (const point of regressionModel.dots) {
        if (Number.isFinite(point.y)) yMax = Math.max(yMax, point.y);
      }
    }
    if (showTarget) yMax = Math.max(yMax, TARGET_DOT.y);

    const veil = [{ start: new Date(), end: localToUTC(endDate), y1: 0, y2: yMax }];

    return defineChart({
      marks: [
        // The stacked month bars own all interaction: clicking anywhere in a
        // column resolves to a segment whose datum carries the month string.
        rect(stackRows, {
          id: "revenue",
          x1: "start",
          x2: "end",
          y1: "y1",
          y2: "y2",
          color: "group",
          inset: 0,
        }),
        // Future months veil, painted over the bars. Decorative so it never
        // steals focus or clicks from the bars underneath it.
        decorative(
          rect(veil, {
            id: "future-veil",
            x1: "start",
            x2: "end",
            y1: "y1",
            y2: "y2",
            fill: "#ffffff66",
            inset: 0,
          }),
        ),
        ...(showRegression
          ? [
              decorative(
                lineY(regressionModel.line, {
                  id: "regression-line",
                  x: "x",
                  y: "y",
                  stroke: colors.text,
                  strokeWidth: 1.5,
                }),
              ),
              decorative(
                dot(regressionModel.dots, {
                  id: "regression-dots",
                  x: "x",
                  y: "y",
                  stroke: "white",
                  strokeWidth: 1.5,
                  fill: colors.text,
                  r: 3,
                }),
              ),
            ]
          : []),
        ...(showTarget
          ? [
              decorative(
                dot([TARGET_DOT], {
                  id: "target",
                  x: "x",
                  y: "y",
                  stroke: "white",
                  strokeWidth: 1.5,
                  fill: colors.pinkVivid,
                  r: 8,
                }),
              ),
            ]
          : []),
      ],
      scales: {
        x: {
          scale: scaleUtc,
          axis: {
            ticks: { size: 0 },
            tickLabels: { fontSize: 12 },
          },
        },
        y: {
          scale: scaleLinear,
          grid: true,
          axis: {
            ticks: { count: 10, size: 0, format: formatAxisNumber },
            tickLabels: { fontSize: 12 },
          },
        },
      },
      margin: { left: 60, bottom: 40 },
      color: {
        domain: colorDomain,
        range: colorDomain.map(group => STREAM_COLOR[group] ?? colors.text3),
      },
      // Clicks anywhere in a month column should resolve to that month, like the
      // old invisible pointerX rule. The old chart had no hover visuals, so no
      // crosshair or focus ring.
      focus: "nearest-x",
      focusRing: false,
      maxFocusDistance: Number.POSITIVE_INFINITY,
    });
  }, [stackRows, regressionModel, showRegression, showTarget]);

  const { ref, height } = useContainerSize();

  return (
    <Box css={{ height: "100%", display: "flex", gap: 16 }}>
      <Box css={{ backgroundColor: "white", padding: 16, borderRadius: 16, width: 250, spaceY: 12 }}>
        <Select
          value={selectedCurrency}
          onValueChange={t =>
            setQueryOptions(
              t === CONSOLIDATED
                ? { ...mrrQueryOptions, consolidated: true, tokenCurrency: undefined }
                : { ...mrrQueryOptions, consolidated: false, tokenCurrency: t },
            )
          }
        >
          <option value={CONSOLIDATED}>All currencies (NZD)</option>
          {tokenCurrencies.map(code => (
            <option key={code} value={code}>
              {tokenCurrencyToMoneyCurrency(code)}
            </option>
          ))}
        </Select>
        <Field css={{ flexDirection: "row" }}>
          <Label css={{ flex: "1 0 0px" }}>Amortize Recurring</Label>
          <Checkbox
            checked={mrrQueryOptions.amortizeRecurring}
            onCheckedChange={c => setQueryOptions({ ...mrrQueryOptions, amortizeRecurring: c })}
          />
        </Field>
        <Field css={{ flexDirection: "row" }}>
          <Label css={{ flex: "1 0 0px" }}>Amortize One-offs</Label>
          <Checkbox
            checked={mrrQueryOptions.amortizeOneOffs}
            onCheckedChange={c => setQueryOptions({ ...mrrQueryOptions, amortizeOneOffs: c })}
          />
        </Field>
        <Field css={{ flexDirection: "row" }}>
          <Label css={{ flex: "1 0 0px" }}>Include Givenwell Supporters</Label>
          <Checkbox
            checked={mrrQueryOptions.includeGivenwell}
            onCheckedChange={c => setQueryOptions({ ...mrrQueryOptions, includeGivenwell: c })}
          />
        </Field>
        <Separator css={{ my: 32 }} />
        {STREAMS.map(stream => (
          <Field key={stream.key} css={{ flexDirection: "row" }}>
            <Label css={{ flex: "1 0 0px" }}>Show {stream.label}</Label>
            <Checkbox
              checked={visibleStreams[stream.key]}
              onCheckedChange={c => setVisibleStreams({ ...visibleStreams, [stream.key]: c })}
            />
          </Field>
        ))}

        <Separator css={{ my: 32 }} />
        <Field css={{ flexDirection: "row" }}>
          <Label css={{ flex: "1 0 0px" }}>Show Regression Line</Label>
          <Checkbox checked={showRegression} onCheckedChange={c => setShowRegression(c)} />
        </Field>
        <Field css={{ flexDirection: "row" }}>
          <Label css={{ flex: "1 0 0px" }}>Show Target</Label>
          <Checkbox checked={showTarget} onCheckedChange={c => setShowTarget(c)} />
        </Field>
        <Box css={{ fontScale: "sm" }}>
          <Text>{Math.round(monthOnMonthGrowth * 1000) / 10}% MoM growth</Text>
          <Text>{Math.round(yearOnYearGrowth * 100)}% YoY growth</Text>
          <Text>MRR doubles in {Math.round(timeToDouble * 10) / 10} months</Text>
          <Text>MRR 10x in {Math.round(timeTo10x * 10) / 10} months</Text>
        </Box>

        {isFetching && <Box>Loading...</Box>}
      </Box>
      <Box
        css={{
          flex: "1 0 0px",
          display: "flex",
          flexDirection: "column",
          backgroundColor: colors.white,
          borderRadius: 16,
          pt: 16,
        }}
      >
        <Legend />
        <Box css={{ flex: "1 0 0px", position: "relative" }}>
          <ChartBox ref={ref} css={{ width: "100%", height: "100%", overflow: "hidden" }}>
            {height !== undefined && (
              <Chart
                definition={definition}
                height={height}
                ariaLabel="Monthly recurring revenue by stream"
                onSelect={point => {
                  // Month drill-down is per-currency (allowance detail); not available in consolidated mode.
                  if (point && "month" in point.datum && !mrrQueryOptions.consolidated) {
                    setActiveMonth(point.datum.month);
                  }
                }}
              />
            )}
          </ChartBox>
        </Box>
      </Box>
      {activeMonth && (
        <MonthDetailDialog
          tokenCurrency={mrrQueryOptions.tokenCurrency!}
          month={activeMonth}
          open={true}
          queryOptions={mrrQueryOptions}
          onOpenChange={o => {
            if (!o) {
              setActiveMonth(null);
            }
          }}
        />
      )}
    </Box>
  );
}

function Legend() {
  return (
    <Flex css={{ gap: 16, pl: 60, flexWrap: "wrap" }}>
      {STREAMS.map(item => (
        <Box key={item.key} css={{ display: "flex", alignItems: "center", fontScale: "sm" }}>
          <Box css={{ size: 16, borderRadius: 9999, backgroundColor: item.color, mr: 6 }} />
          {item.label}
        </Box>
      ))}
    </Flex>
  );
}

function MonthDetailDialog({
  month,
  open,
  onOpenChange,
  tokenCurrency,
  queryOptions,
}: {
  month: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tokenCurrency: string;
  queryOptions: MRRQueryOptions;
}) {
  const { data } = useQuery(
    monthDrillDownQuery({
      month: month,
      tokenCurrency: tokenCurrency,
      amortizeRecurring: queryOptions.amortizeRecurring,
      amortizeOneOffs: queryOptions.amortizeOneOffs,
      includeGivenwell: queryOptions.includeGivenwell,
    }),
  );

  const fmt = new Intl.DateTimeFormat("en-NZ", {
    month: "long",
    year: "numeric",
  });

  return (
    <Dialog
      title={fmt.format(new Date(month))}
      open={open}
      onOpenChange={onOpenChange}
      css={{ maxWidth: 520 }}
      content={
        <Box>
          {data ? (
            <Box css={{ display: "grid", gridTemplateColumns: "auto 1fr auto", columnGap: 16 }}>
              {data.monthly.length > 0 && (
                <LineItems currency={data.currency} title="Monthly" items={data.monthly || []} />
              )}
              {data.quarterly.length > 0 && (
                <LineItems currency={data.currency} title="Quarterly" items={data.quarterly || []} />
              )}
              {data.annually.length > 0 && (
                <LineItems currency={data.currency} title="Annual" items={data.annually || []} />
              )}
              {data.one_offs.length > 0 && (
                <LineItems currency={data.currency} title="One-off" items={data.one_offs || []} />
              )}

              <Box css={{ colSpan: 2, weight: 600, mt: 8 }}>Total</Box>
              <Box css={{ mt: 8 }}>{formatCents(data.total_in_minimal_denomination || 0, data.currency)}</Box>
            </Box>
          ) : (
            <Box>Loading...</Box>
          )}
        </Box>
      }
    />
  );
}

function LineItems({ title, items, currency }: { title: string; items: DrillDownItem[]; currency: string }) {
  const dayFmt = new Intl.DateTimeFormat("en-NZ", {
    day: "2-digit",
    month: "short",
  });
  const sortedItems = [...items];
  sortedItems.sort((a, b) => a.supporter_name.localeCompare(b.supporter_name));
  sortedItems.sort((a, b) => new Date(a.next_date).getTime() - new Date(b.next_date).getTime());
  return (
    <Fragment>
      <Box css={{ weight: 600, colSpan: 3, mt: 8 }}>{title}</Box>

      {sortedItems.map((row, i) => (
        <Fragment key={i}>
          <Box>{dayFmt.format(new Date(row.next_date))}</Box>
          <Box>{row.supporter_name}</Box>
          <Box css={{ textAlign: "right" }}>{formatCents(row.amount_in_minimal_denomination, currency)}</Box>
        </Fragment>
      ))}
    </Fragment>
  );
}
